/**
 * SAT Toast - the one thing a failed request is allowed to say.
 *
 * Backend messages are written for us, not for customers: they carry HTTP
 * statuses, JSON payloads and, when our API proxies something else, that
 * upstream's internals. None of that belongs on screen, and passing
 * `err.message` through meant any of it could land there.
 *
 * So the flow shows one generic line instead - every screen already offers a
 * way forward (Try again, Retake, Start over), which is what the customer
 * actually needs. The real message still goes to the console, and every
 * request is recorded by sat-api.js for SATDebug.dump().
 */
(function (global) {
  "use strict";

  var DEFAULT_MESSAGE = "Something went wrong. Please try again.";
  var VISIBLE_MS = 4000;

  var el = null;
  var hideTimer = null;

  function ensureEl() {
    if (el && el.isConnected) return el;
    el = document.createElement("div");
    el.className = "sat-toast";
    // Announced, but never focus-stealing - the customer is usually mid-task.
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.hidden = true;
    document.body.appendChild(el);
    return el;
  }

  /** One toast at a time: a second failure replaces the first. */
  function show(message) {
    var node = ensureEl();
    node.textContent = message || DEFAULT_MESSAGE;
    node.hidden = false;
    // Reflow so the transition runs again on a toast that is already up.
    void node.offsetWidth;
    node.classList.add("sat-toast--in");

    clearTimeout(hideTimer);
    hideTimer = setTimeout(hide, VISIBLE_MS);
  }

  function hide() {
    if (!el) return;
    el.classList.remove("sat-toast--in");
    // Stay in the DOM until the fade finishes, then go back to hidden so it is
    // out of the accessibility tree.
    clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      if (el) el.hidden = true;
    }, 200);
  }

  /**
   * The failure path for anything thrown by an API call. `context` is a label
   * for the console only - it never reaches the customer.
   */
  function error(err, context) {
    console.error("[SAT]" + (context ? " " + context + ":" : ""), err);
    show(DEFAULT_MESSAGE);
  }

  /**
   * For Shopify's own cart errors, which are the one source in this flow
   * written for customers rather than for us - "sold out", "all N of X are
   * already in your cart". Those say something the generic line cannot, so
   * they are shown as-is.
   *
   * This is deliberately not the default: it applies to Shopify's cart
   * endpoints only, never to the assessment API. Anything unexpected still
   * falls back to the generic message rather than printing a bare Error.
   */
  function fromCart(err, context) {
    console.error("[SAT]" + (context ? " " + context + ":" : ""), err);
    var text = err && typeof err.message === "string" ? err.message.trim() : "";
    show(text || DEFAULT_MESSAGE);
  }

  global.SATToast = {
    DEFAULT_MESSAGE: DEFAULT_MESSAGE,
    show: show,
    hide: hide,
    error: error,
    fromCart: fromCart,
  };
})(window);
